import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiFilter } from 'react-icons/fi';
import { supabase } from '../../supabase/supabase';
import { useAuth } from '../../contexts/AuthContext';
import './Transaction.css';
import FloatingSidebarButton from "../../components/FloatingSidebarButton/FloatingSidebarButton";
import { useLanguage } from "../../contexts/LanguageContext";

const DEFAULT_AVATAR =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjQiIGN5PSIyNCIgcj0iMjQiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMiIgeT0iMTIiIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Rya2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPgo8cGF0aCBkPSJNMjAgMjF2LTJhNCA0IDAgMCAwLTQtNEg4YTQgNCAwIDAgMC00IDR2MiIvPgo8Y2lyY2xlIGN4PSIxMiIgY3k9IjciIHI9IjQiLz4KPC9zdmc+Cjwvc3ZnPg==";

function resolveAvatarUrl(userId, avatarFilename) {
  if (!avatarFilename) return DEFAULT_AVATAR;
  if (typeof avatarFilename === "string" && avatarFilename.startsWith("data:")) return avatarFilename;
  if (typeof avatarFilename === "string" && /^https?:\/\//i.test(avatarFilename)) return avatarFilename;
  try {
    const { data } = supabase.storage.from("avatars").getPublicUrl(`${userId}/${avatarFilename}`);
    return data?.publicUrl || DEFAULT_AVATAR;
  } catch {
    return DEFAULT_AVATAR;
  }
}

// ---------- helpers ----------
const asNumber = (v) => {
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toDate = (ts) => {
  if (!ts) return null;
  if (typeof ts?.toDate === 'function') return ts.toDate();
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (timestamp) => {
  const d = toDate(timestamp);
  if (!d) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} @ ${hours}:${minutes}`;
};

// Get date range for filter
const getDateRange = (filterType) => {
  const now = new Date();
  const start = new Date();
  
  switch (filterType) {
    case 'today':
      start.setHours(0, 0, 0, 0);
      break;
    case 'thisWeek':
      const dayOfWeek = now.getDay();
      start.setDate(now.getDate() - dayOfWeek);
      start.setHours(0, 0, 0, 0);
      break;
    case 'thisMonth':
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      break;
    case 'last3Months':
      start.setMonth(now.getMonth() - 3);
      start.setHours(0, 0, 0, 0);
      break;
    case 'last6Months':
      start.setMonth(now.getMonth() - 6);
      start.setHours(0, 0, 0, 0);
      break;
    case 'lastYear':
      start.setFullYear(now.getFullYear() - 1);
      start.setHours(0, 0, 0, 0);
      break;
    case 'all':
    default:
      return null;
  }
  
  return start.toISOString();
};

const Transaction = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [userProfiles, setUserProfiles] = useState(new Map()); // userId -> { username, display_name, avatar_url }
  const filterRef = useRef(null);
  const { translations: t } = useLanguage();

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };

    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFilterDropdown]);

  // Fetch user profiles for sender/receiver IDs
  const fetchUserProfiles = async (userIds) => {
    if (!userIds || userIds.length === 0) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, username, display_name, avatar_url')
        .in('user_id', Array.from(userIds));

      if (error) {
        console.error('Error fetching user profiles:', error);
        return;
      }

      const profilesMap = new Map();
      (data || []).forEach((profile) => {
        profilesMap.set(profile.user_id, {
          username: profile.username || 'user',
          display_name: profile.display_name || profile.username || 'User',
          avatar_url: profile.avatar_url,
        });
      });

      setUserProfiles((prev) => {
        const merged = new Map(prev);
        profilesMap.forEach((value, key) => merged.set(key, value));
        return merged;
      });
    } catch (err) {
      console.error('Failed to fetch user profiles:', err);
    }
  };

  // Fetch transactions from Supabase transactions table
  useEffect(() => {
    // Mark wallet as verified when entering transaction page
    sessionStorage.setItem('walletVerified', 'true');
    
    if (!currentUser?.id) {
      setDocs([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Fetch initial transactions
    const fetchTransactions = async () => {
      try {
        let query = supabase
          .from('transactions')
          .select('*')
          .eq('user_id', currentUser.id)
          .eq('field', 'winicoins');

        // Apply date filter
        const dateRange = getDateRange(filterType);
        if (dateRange) {
          query = query.gte('created_at', dateRange);
        }

        const { data, error } = await query.order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching transactions:', error);
          setDocs([]);
          setLoading(false);
          return;
        }

        const rows = (data || []).map((tx) => ({
          id: tx.id,
          path: `transactions/${tx.user_id}/${tx.id}`, // For deduplication
          ...tx,
        }));

        setDocs(rows);

        // Collect unique user IDs (sender_id and receiver_id) for profile fetching
        const userIds = new Set();
        rows.forEach((tx) => {
          if (tx.sender_id && tx.sender_id !== currentUser.id) userIds.add(tx.sender_id);
          if (tx.receiver_id && tx.receiver_id !== currentUser.id) userIds.add(tx.receiver_id);
        });

        // Fetch profiles for these users
        if (userIds.size > 0) {
          await fetchUserProfiles(Array.from(userIds));
        }

        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch transactions:', err);
        setDocs([]);
        setLoading(false);
      }
    };

    fetchTransactions();

    // Set up real-time subscription
    const channel = supabase
      .channel('transactions-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transactions',
          filter: `user_id=eq.${currentUser.id}`
        },
        (payload) => {
          console.log('Transaction change received:', payload);
          if (payload.new && payload.new.field === 'winicoins') {
            // Refetch transactions when changes occur
            fetchTransactions();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUser?.id, filterType]);

  // Build display rows: ONLY my transactions, no duplicates, sign derived by me
  const transactions = useMemo(() => {
    if (!currentUser?.id) return [];

    // Deduplicate by full path just in case
    const map = new Map();
    for (const d of docs) map.set(d.path || d.id, d);

    const arr = Array.from(map.values()).map((tx) => {
      // Determine if this is an outgoing transaction
      const isOutgoing = tx.flow === 'out' || tx.sender_id === currentUser.id;
      
      // Get amount (already positive in Supabase)
      const magnitude = Math.abs(asNumber(tx.amount));

      // Determine which user to show (sender or receiver)
      let otherUserId = null;
      let otherUserProfile = null;
      
      if (isOutgoing) {
        // Outgoing: show receiver
        otherUserId = tx.receiver_id;
      } else {
        // Incoming: show sender
        otherUserId = tx.sender_id;
      }

      if (otherUserId && userProfiles.has(otherUserId)) {
        otherUserProfile = userProfiles.get(otherUserId);
      }

      // Get avatar URL
      let avatarUrl = DEFAULT_AVATAR;
      if (otherUserProfile && otherUserId) {
        avatarUrl = resolveAvatarUrl(otherUserId, otherUserProfile.avatar_url);
      }

      // Get display name (username)
      let displayName = tx.source || (t.trans?.walletSource || "wallet");
      if (otherUserProfile) {
        displayName = otherUserProfile.display_name || otherUserProfile.username || 'User';
      }

      // Get transaction type (Transfer, Received, etc.)
      let transactionType = tx.source || 'wallet';
      if (tx.source === 'transfer' || tx.source === 'wallet_transfer') {
        transactionType = isOutgoing ? 'Transfer' : 'Received';
      } else {
        // Capitalize first letter of source
        transactionType = tx.source ? tx.source.charAt(0).toUpperCase() + tx.source.slice(1) : 'Wallet';
      }

      return {
        id: tx.id,
        source: tx.source || (t.trans?.walletSource || "wallet"),
        time: tx.created_at || tx.time, // Use created_at from Supabase
        isOutgoing: isOutgoing,
        amount: magnitude,
        avatarUrl: avatarUrl,
        displayName: displayName,
        transactionType: transactionType,
        otherUserId: otherUserId,
      };
    });

    // newest first
    arr.sort((a, b) => {
      const da = toDate(a.time)?.getTime() ?? 0;
      const db = toDate(b.time)?.getTime() ?? 0;
      return db - da;
    });

    return arr;
  }, [docs, currentUser?.id, t, userProfiles]);

  if (loading) {
    return (
      <div className="transaction-page">
              <div className="transaction-loading">{t.trans?.loading || "Loading..."}</div>
      </div>
    );
  }

  return (
    <div className="transaction-page">
      <FloatingSidebarButton userProfile={userProfile} />
      {/* Header Bar */}
      <div className="transaction-header">
        <button
          className="transaction-back"
          onClick={() => navigate(-1)}
          aria-label={t.trans?.back || "Back"}
          title={t.trans?.back || "Back"}
        >
          <FiArrowLeft />
        </button>
        <h1 className="transaction-title">{t.trans?.title || "My Winis"}</h1>
        <div className="transaction-filter-wrapper" ref={filterRef}>
          <button 
            className="transaction-filter"  
            aria-label={t.trans?.filter || "Filter"} 
            title={t.trans?.filter || "Filter"}
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
          >
            <FiFilter />
          </button>
          {showFilterDropdown && (
            <div className="transaction-filter-dropdown">
              <button
                className={`transaction-filter-option ${filterType === 'today' ? 'active' : ''}`}
                onClick={() => {
                  setFilterType('today');
                  setShowFilterDropdown(false);
                }}
              >
                Today
              </button>
              <button
                className={`transaction-filter-option ${filterType === 'thisWeek' ? 'active' : ''}`}
                onClick={() => {
                  setFilterType('thisWeek');
                  setShowFilterDropdown(false);
                }}
              >
                This Week
              </button>
              <button
                className={`transaction-filter-option ${filterType === 'thisMonth' ? 'active' : ''}`}
                onClick={() => {
                  setFilterType('thisMonth');
                  setShowFilterDropdown(false);
                }}
              >
                This Month
              </button>
              <button
                className={`transaction-filter-option ${filterType === 'last3Months' ? 'active' : ''}`}
                onClick={() => {
                  setFilterType('last3Months');
                  setShowFilterDropdown(false);
                }}
              >
                Last 3 Months
              </button>
              <button
                className={`transaction-filter-option ${filterType === 'last6Months' ? 'active' : ''}`}
                onClick={() => {
                  setFilterType('last6Months');
                  setShowFilterDropdown(false);
                }}
              >
                Last 6 Months
              </button>
              <button
                className={`transaction-filter-option ${filterType === 'lastYear' ? 'active' : ''}`}
                onClick={() => {
                  setFilterType('lastYear');
                  setShowFilterDropdown(false);
                }}
              >
                Last Year
              </button>
              <button
                className={`transaction-filter-option ${filterType === 'all' ? 'active' : ''}`}
                onClick={() => {
                  setFilterType('all');
                  setShowFilterDropdown(false);
                }}
              >
                All
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Transaction List */}
      <div className="transaction-list">
        {transactions.length === 0 ? (
          <div className="no-transactions">
            <p>
              {(() => {
                const filterLabels = {
                  today: 'Today',
                  thisWeek: 'This Week',
                  thisMonth: 'This Month',
                  last3Months: 'Last 3 Months',
                  last6Months: 'Last 6 Months',
                  lastYear: 'Last Year',
                  all: '',
                };
                const filterLabel = filterLabels[filterType] || '';
                return filterLabel 
                  ? `${t.trans?.empty || "No transactions available"} ${filterLabel}`
                  : (t.trans?.empty || "No transactions available");
              })()}
            </p>
          </div>
        ) : (
                     transactions.map((tx) => (
             <div 
               key={tx.id} 
               className="transaction-item"
               onClick={() => navigate(`/trans-details/${tx.id}`)}
               style={{ cursor: 'pointer' }}
             >
               <div className="transaction-avatar">
                 <img src={tx.avatarUrl} alt={tx.displayName} />
               </div>

               <div className="transaction-details">
                 <div className="transaction-username">{tx.displayName}</div>
                 <div className="transaction-type">{tx.transactionType}</div>
               </div>

               <div className="transaction-amount">
                 <div className={`amount ${tx.isOutgoing ? 'negative' : 'positive'}`}>
                   {tx.isOutgoing ? '-' : '+'} {asNumber(tx.amount).toFixed(2)}
                 </div>
                 <div className="currency">winis</div>
               </div>
             </div>
           ))
        )}
      </div>
    </div>
  );
};

export default Transaction;
